import type { LoaderFunctionArgs } from '@remix-run/node'
import { useLoaderData, useLocation } from '@remix-run/react'
import { useEffect } from 'react'
import {
  aDrawnFeature,
  aMapDrawIsOpen,
  aMapDrawRef,
  aMapFilters,
  aMapRef,
  aPersistedFilters,
  useAtomValue,
  useSetAtom,
} from '~/atoms'
import { useEmbed } from '~/hooks'
import { locationToFeature } from '~/models'
import { findSearch } from '~/mongo.server'
import { getOrganizationSlug } from '~/utils.server'
import Container from './Container'
export { ErrorBoundary } from '~/components'

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const organizationSlug = getOrganizationSlug(request)
  const url = new URL(request.url)
  const searchParams = url.searchParams
  const searchSlug = searchParams.get('savedSearch')
  if (searchSlug) {
    const search = await findSearch({
      'organization.slug': organizationSlug,
      slug: searchSlug,
    })
    if (search) {
      return search
    }
  }
  return null
}

export default function MapLayout() {
  const search = useLoaderData<typeof loader>()
  const setDrawnFeature = useSetAtom(aDrawnFeature)
  const mapDrawRef = useAtomValue(aMapDrawRef)
  const mapRef = useAtomValue(aMapRef)
  const setIsDrawOpen = useSetAtom(aMapDrawIsOpen)
  const setMapFilters = useSetAtom(aMapFilters)
  const setPersistedFilters = useSetAtom(aPersistedFilters)

  const { isEmbed, updateHostPageURL } = useEmbed()
  const location = useLocation()

  useEffect(() => {
    if (isEmbed && location.pathname === '/') {
      updateHostPageURL()
    }
  }, [isEmbed, location.pathname, updateHostPageURL])

  useEffect(() => {
    if (search && mapRef && mapDrawRef) {
      setTimeout(() => {
        mapRef.flyTo({
          center: [search.lng, search.lat],
          zoom: search.z,
          duration: 500,
        })
        setPersistedFilters({})
        setMapFilters(search.filters)

        const geometry = search.location.$geoWithin.$geometry
        const isDrawnFeature = 'properties' in geometry

        if (mapDrawRef) {
          mapDrawRef.trash()
          mapDrawRef.deleteAll()
          if (isDrawnFeature) {
            setIsDrawOpen(true)
            const feature = locationToFeature(
              search.location.$geoWithin.$geometry
            )
            setDrawnFeature(feature)
            mapDrawRef.add(feature)
          } else {
            setDrawnFeature(null)
          }
        }
      }, 200)
    }
  }, [
    search,
    mapRef,
    setMapFilters,
    setDrawnFeature,
    mapDrawRef,
    setIsDrawOpen,
  ])

  return <Container />
}
